import { atom, selector, selectorFamily } from 'recoil';
import { APP_NOTIFICATION_CATEGORY_LABELS } from '../constants/appNotifications';
import { InternalEventSetting } from '../types/eventSetting';
import { getEventSettingInternalId } from '../utils/eventSettings';
import { accountState } from './account';

export const eventSettingsState = selector<InternalEventSetting[] | undefined>({
  key: 'app.client.eventSettings',
  get: ({ get }) => {
    const account = get(accountState);
    if (!account) return;
    return account.notificationSettings.map((eventSetting) => ({
      ...eventSetting,
      internalId: getEventSettingInternalId(eventSetting),
    }));
  },
});

export const eventSettingsCountState = selector<string>({
  key: 'app.client.eventSettingsCount',
  get: ({ get }) => {
    const eventsCount = get(eventSettingsState)?.length ?? 0;
    const filteredCount = get(filteredEventSettingsState)?.length ?? 0;
    return `Mostrando ${filteredCount} de ${eventsCount}`;
  },
});

export const eventSettingState = selectorFamily<InternalEventSetting | undefined, string>({
  key: 'app.client.eventSetting',
  get:
    (internalId) =>
    ({ get }) => {
      const events = get(eventSettingsState);
      if (!events) return;
      return events.find((event) => event.internalId === internalId);
    },
});

export const selectedEventSettingState = atom<string | undefined>({
  key: 'app.client.eventSettings.selected',
  default: undefined,
});

export const eventSettingsByCategoriesState = selector<
  { category: string; categoryDisplay: string; settings: InternalEventSetting[] | undefined }[] | undefined
>({
  key: 'app.client.eventSettingCategories.by',
  get: ({ get }) => {
    const events = get(filteredEventSettingsState);
    if (!events) return;
    return Object.entries(APP_NOTIFICATION_CATEGORY_LABELS)
      .map(([category, categoryDisplay]) => {
        return {
          category,
          categoryDisplay,
          settings: get(eventSettingsInCategoryState(category)),
        };
      })
      .filter((item) => !!item.settings?.length);
  },
});

export const eventSettingsInCategoryCountState = selectorFamily<number | undefined, string>({
  key: 'app.client.eventSettingInCategory.count',
  get:
    (category) =>
    ({ get }) => {
      const events = get(eventSettingsState);
      if (!events) return;

      return events.filter((event) => event.category === category).length;
    },
});

export const eventSettingsInCategoryState = selectorFamily<InternalEventSetting[] | undefined, string>({
  key: 'app.client.eventSettingCategories.in',
  get:
    (category) =>
    ({ get }) => {
      const events = get(filteredEventSettingsState);
      if (!events) return;

      return events.filter((event) => event.category === category);
    },
});

const filteredEventSettingsState = selector<InternalEventSetting[] | undefined>({
  key: 'app.client.eventSettings.filtered',
  get: ({ get }) => {
    const events = get(eventSettingsState);
    if (!events) return;
    let filteredEvents = [...events];

    const sortEventsByName = (a: InternalEventSetting, b: InternalEventSetting) => {
      return a.notificationDescription.localeCompare(b.notificationDescription);
    };

    const active = get(activeEventSettingsState);

    if (active) {
      filteredEvents = filteredEvents.filter((event) => {
        return !!event.days.length;
      });
    }

    const search = get(searchEventSettingsState);

    if (search) {
      filteredEvents = filteredEvents.filter((event) => {
        if (event.notificationDescription.toLowerCase().includes(search.toLowerCase())) return true;
        if (event.categoryDisplay.toLowerCase().includes(search.toLowerCase())) return true;
        return false;
      });
    }

    return filteredEvents.sort(sortEventsByName);
  },
});

export const searchEventSettingsState = atom({
  key: 'app.client.eventSettings.search',
  default: '',
});

export const activeEventSettingsState = atom({
  key: 'app.client.eventSettings.active',
  default: false,
});

export const editingEventSettingState = atom<InternalEventSetting | undefined>({
  key: 'app.client.eventSettings.editing',
  default: selector({
    key: 'app.client.eventSettings.editing.initializer',
    get: ({ get }) => {
      const internalId = get(selectedEventSettingState);
      if (internalId === undefined) return;
      return get(eventSettingState(internalId));
    },
  }),
});
